/**
 * VA Guides home page — onboarding + daily work (simple English for PH VAs).
 * Guides are linked by title keywords from admin-published guides.
 */

export const VA_HOME_INTRO = {
  title: 'Start here',
  subtitle: 'Follow these steps in order before you do daily following work.',
  note: 'Click “Open guide” on each step. The guide opens right here with screenshots. Do not skip steps.'
};

export const VA_HOME_SETUP = {
  title: 'Setup accounts',
  highlight: 'Finish this for all accounts first!',
  stepsLabel: 'Step 1'
};

export const VA_HOME_MANAGER_GATE = {
  title: 'STOP — contact your manager first',
  message:
    'Do not start daily work until you finish ALL setup steps for ALL 100 accounts. Message your manager when setup is done. Wait for their OK before you begin following.'
};

export const VA_HOME_ONBOARDING_STEPS = [
  {
    step: 1,
    title: 'Update every profile',
    summary: 'For each account, update privacy, profile photo, banner, name, bio, birthday, and username.',
    guideMatchers: [],
    subGuides: [
      {
        label: 'Open privacy settings',
        guideMatchers: ['privacy settings', 'open privacy']
      },
      {
        label: 'Update Profile',
        guideMatchers: ['update profile']
      },
      {
        label: 'Change username',
        guideMatchers: ['change username']
      }
    ],
    categoryHint: 'X Interface'
  }
];

export const VA_HOME_DAILY_WORK = {
  title: 'Daily work',
  subtitle: 'Do this every working day.',
  overview: [
    'You manage 100 accounts.',
    'Each account must follow 40 new users per day.',
    'Work in small batches in GoLogin (5–10 accounts at a time).'
  ],
  rules: [
    'Open 5–10 accounts in GoLogin at the same time (15–20 only if your PC is fast).',
    'Use only the target usernames to train the Algorithm of each of your accounts. (Like 3–4 posts of 3–4 target usernames)',
    'Follow in order: Account 1 → Account 2 → … → Account 100.',
    'Do not burst follow. Follow MAX 4–5 users per account and move to the next account to SPREAD the following.'
  ],
  process: [
    'Open the first batch (5–10 accounts) in GoLogin.',
    'Go to Account 1. Follow 4–5 users from the comment section in the posts of the For You page.',
    'Go to Account 2. Follow 4–5 users. Then Account 3, 4, 5… until the batch is done.',
    'Close that batch. Open the next batch of 5–10 accounts.',
    'Repeat until all 100 accounts have followed 4–5 users each.',
    'Start again from Account 1. Repeat the full cycle until every account has 40 follows today.'
  ],
  target: {
    label: 'Daily target',
    math: '100 accounts × 40 follows = 4,000 follows per day'
  },
  tips: [
    'Work account by account in order (1 to 100).',
    'Only follow users that are commenting on posts of a Trained Algorithm (onlyfans girls on For You page).',
    'If your PC is slow with 10 accounts, use 5 at a time.',
    'If you have a problem, tell your manager right away.'
  ]
};

/** Default home page structure (mirrors backend vaGuidesHomeDefaults.js). */
export function getDefaultGuidesHome() {
  return {
    intro: { ...VA_HOME_INTRO, note: 'Click “Open guide” on each step. The guide opens right here with screenshots and videos. Do not skip steps.' },
    sections: [{
      title: VA_HOME_SETUP.title,
      highlight: VA_HOME_SETUP.highlight,
      stepsLabel: VA_HOME_SETUP.stepsLabel,
      order: 0,
      steps: VA_HOME_ONBOARDING_STEPS.map((step) => ({
        ...step,
        guideId: null,
        guideLabel: step.guideLabel || '',
        linkedGuides: (step.subGuides || []).map((sub) => ({
          label: sub.label,
          guideId: null,
          guideMatchers: sub.guideMatchers
        }))
      }))
    }],
    managerGate: { ...VA_HOME_MANAGER_GATE },
    dailyWork: { ...VA_HOME_DAILY_WORK }
  };
}

export function resolveGuidesHome(stored) {
  if (!stored || typeof stored !== 'object') return getDefaultGuidesHome();
  const defaults = getDefaultGuidesHome();
  const hasSections = Array.isArray(stored.sections) && stored.sections.length > 0;
  if (!hasSections && !stored.intro?.title) return defaults;
  return {
    intro: { ...defaults.intro, ...(stored.intro || {}) },
    sections: hasSections
      ? stored.sections.map((section, i) => ({
        ...section,
        order: section.order ?? i,
        steps: (section.steps || []).map((step, j) => ({
          ...step,
          step: step.step ?? j + 1,
          linkedGuides: (step.linkedGuides || []).map((lg) => ({ ...lg }))
        }))
      }))
      : defaults.sections,
    managerGate: { ...defaults.managerGate, ...(stored.managerGate || {}) },
    dailyWork: {
      ...defaults.dailyWork,
      ...(stored.dailyWork || {}),
      target: { ...defaults.dailyWork.target, ...(stored.dailyWork?.target || {}) },
      overview: stored.dailyWork?.overview?.length ? stored.dailyWork.overview : defaults.dailyWork.overview,
      rules: stored.dailyWork?.rules?.length ? stored.dailyWork.rules : defaults.dailyWork.rules,
      process: stored.dailyWork?.process?.length ? stored.dailyWork.process : defaults.dailyWork.process,
      tips: stored.dailyWork?.tips?.length ? stored.dailyWork.tips : defaults.dailyWork.tips
    }
  };
}

function normalizeTitle(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function titleMatches(guideTitle, matchers) {
  if (!matchers?.length) return false;
  const t = normalizeTitle(guideTitle);
  return matchers.some((needle) => t.includes(normalizeTitle(needle)));
}

export function collectAllGuides(categories = [], uncategorizedGuides = []) {
  const fromCategories = categories.flatMap((cat) =>
    (cat.guides || []).map((guide) => ({
      ...guide,
      categoryTitle: cat.title
    }))
  );
  const uncategorized = (uncategorizedGuides || []).map((guide) => ({
    ...guide,
    categoryTitle: null
  }));
  return [...fromCategories, ...uncategorized].filter((g) => g.published !== false);
}

export function findGuide(guides, matchers) {
  if (!matchers?.length) return null;
  return guides.find((g) => titleMatches(g.title, matchers)) || null;
}

export function findGuideById(guides, guideId) {
  if (!guideId) return null;
  return guides.find((g) => String(g._id) === String(guideId)) || null;
}

/** Resolve a linked guide by id first, then legacy title matchers. */
export function resolveLinkedGuide(guides, link) {
  if (!link) return null;
  const byId = findGuideById(guides, link.guideId);
  if (byId) return byId;
  if (link.guideMatchers?.length) return findGuide(guides, link.guideMatchers);
  return null;
}

export function resolveStepMainGuide(guides, step) {
  if (!step) return null;
  const byId = findGuideById(guides, step.guideId);
  if (byId) return byId;
  if (step.guideMatchers?.length) return findGuide(guides, step.guideMatchers);
  return null;
}

export function findGuidesForHome(guides) {
  const map = new Map();
  const register = (guide) => {
    if (guide?._id) map.set(String(guide._id), guide);
  };

  VA_HOME_ONBOARDING_STEPS.forEach((step) => {
    const main = findGuide(guides, step.guideMatchers);
    if (main) register(main);
    (step.subGuides || []).forEach((sub) => {
      const g = findGuide(guides, sub.guideMatchers);
      if (g) register(g);
    });
  });

  return map;
}
